import { useRef, useState, useEffect, useCallback } from 'react';
import { TaskColumn } from '../../../../types/topic';

/**
 * Hook for column width calculations with measurements done before render
 * to prevent layout jumps
 */
export function useDndColumn(columns: TaskColumn[]) {
  // Container references
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  
  // State for column width and scroll behavior
  const [columnWidth, setColumnWidth] = useState('');
  const [shouldScroll, setShouldScroll] = useState(false);
  
  // State to track if measurements are complete
  const [measurementComplete, setMeasurementComplete] = useState(false);
  
  // Calculate optimal column width based on container width and column count
  const calculateColumnWidths = useCallback(() => {
    const container = containerRef.current;
    if (!container) return false;
    
    const availableWidth = container.clientWidth;
    const columnCount = columns.length;
    const gap = 16; // gap-4 = 16px
    
    // Calculate total space needed for all columns with minimum width
    const minRequiredWidth = columnCount * 240 + gap * (columnCount - 1);
    
    // If we need more space than available, use fixed width columns with scroll
    if (minRequiredWidth > availableWidth) {
      setColumnWidth('w-[240px] min-w-[240px] flex-shrink-0');
      setShouldScroll(true);
    }
    // If 1-3 columns fit, use proportional width
    else if (columnCount <= 3) {
      if (columnCount === 1) {
        setColumnWidth('flex-1 min-w-[240px]');
      } else if (columnCount === 2) {
        setColumnWidth('w-1/2 min-w-[240px]');
      } else {
        // columnCount === 3
        setColumnWidth('w-1/3 min-w-[240px]');
      }
      setShouldScroll(false);
    }
    // For 4+ columns that fit, use flex growth
    else {
      setColumnWidth('flex-1 min-w-[240px]');
      setShouldScroll(false);
    }
    
    return true;
  }, [columns.length]);
  
  // Take initial measurements before first render
  useEffect(() => {
    setMeasurementComplete(false);
    
    // Use RAF to ensure DOM is ready for measurement
    const rafId = requestAnimationFrame(() => {
      if (calculateColumnWidths()) {
        setMeasurementComplete(true);
      }
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [columns.length, calculateColumnWidths]);
  
  // Setup resize observer for responsive updates
  useEffect(() => {
    if (!measurementComplete) return;
    
    const resizeObserver = new ResizeObserver(() => {
      calculateColumnWidths();
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [measurementComplete, calculateColumnWidths]);
  
  return {
    containerRef,
    boardRef,
    columnWidth,
    shouldScroll,
    measurementComplete,
  };
}
